import React from 'react';
import { AlertCircle } from 'lucide-react';

/**
 * CrossCompanyFreightAlert - Reusable component for cross-company freight detection
 * 
 * @param {Object} props
 * @param {string} props.payableTo - Company receiving the freight payment
 * @param {string} props.payableFrom - Company making the freight payment
 * @param {string} props.value - Current freight value
 * @param {Function} props.onChange - Callback when freight value changes
 * @param {string} props.error - Error message to display
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {string} props.placeholder - Placeholder text for input
 * @param {number} props.maxLength - Maximum length of input
 * @param {string} props.name - Input field name
 */
const CrossCompanyFreightAlert = ({
    payableTo = '',
    payableFrom = '',
    value = '',
    onChange,
    error = '',
    disabled = false,
    placeholder = 'Enter Freight',
    maxLength = 10,
    name = 'cross_company_freight',
    className = ''
}) => {

    const handleInputChange = (e) => {
        let inputValue = e.target.value;

        // Allow "0" but replace it if typing another digit
        if (/^0[1-9]/.test(inputValue)) {
            inputValue = inputValue.replace(/^0+/, ''); // "01" → "1", "05" → "5"
        }

        // Allow "0." for decimals
        if (inputValue === "0" || inputValue === "0.") {
            if (onChange) {
                onChange(name, inputValue);
            }
            return;
        }

        // Validate decimal format (no leading zeros, max 2 decimal places)
        if (/^(?!0\d)\d*(\.\d{0,2})?$/.test(inputValue) || inputValue === '') {
            if (onChange) {
                onChange(name, inputValue);
            }
        }
    };

    return (
        <div
            className={className}
            style={{
                backgroundColor: '#FAF5FF',
                border: '1px solid #E9D4FF',
                borderRadius: '8px',
                padding: '16px',
                marginTop: '16px',
                marginBottom: '16px',
                width: "75%"
            }}
        >
            {/* Alert Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px'
            }}>
                <AlertCircle
                    size={20}
                    style={{
                        color: '#A855F7',
                        flexShrink: 0
                    }}
                />
                <span style={{
                    color: '#8200E0',
                    fontSize: '13px',
                    fontWeight: 400,
                    lineHeight: '1.4'
                }}>
                    Cross Company Detected:{' '}
                    <span style={{ fontWeight: 400 }}>Freight Payable</span>
                    {' '}to{' '}
                    <span style={{ fontWeight: 700 }}>{payableTo}</span>
                    {' '}from{' '}
                    <span style={{ fontWeight: 700 }}>{payableFrom}</span>
                </span>
            </div>

            {/* Freight Input */}
            <div style={{
                backgroundColor: 'white',
                border: error ? '1px solid #EF4444' : '1px solid #E5E7EB',
                borderRadius: '6px',
                padding: '8px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
            }}>
                <span style={{
                    color: '#6B7280',
                    fontSize: '14px',
                    fontWeight: 500
                }}>
                    ₹
                </span>
                <input
                    type="text"
                    name={name}
                    value={value}
                    onChange={handleInputChange}
                    placeholder={placeholder}
                    disabled={disabled}
                    maxLength={maxLength}
                    style={{
                        border: 'none',
                        outline: 'none',
                        width: '100%',
                        fontSize: '14px',
                        color: disabled ? '#9CA3AF' : '#1F2937',
                        backgroundColor: 'transparent',
                        cursor: disabled ? 'not-allowed' : 'text'
                    }}
                />
            </div>

            {/* Error Message */}
            {error && (
                <div style={{
                    color: '#EF4444',
                    fontSize: '12px',
                    marginTop: '6px',
                    fontWeight: 500
                }}>
                    {error}
                </div>
            )}
        </div>
    );
};

export default CrossCompanyFreightAlert